import { useState } from "react";
import { motion } from "framer-motion";
import {
  FaCalendarAlt,
  FaClock,
  FaArrowLeft,
  FaShare,
  FaBookmark,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import BlogContent from "./BlogContent";
import appleImage from "../../assets/images/apple.jpg";

const FloatingParticle = ({ delay }) => (
  <motion.div
    className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
    initial={{ opacity: 0, x: Math.random() * 100, y: Math.random() * 100 }}
    animate={{
      opacity: [0, 1, 0],
      x: Math.random() * 200,
      y: Math.random() * 200,
    }}
    transition={{
      duration: 4,
      repeat: Infinity,
      delay,
      ease: "easeInOut",
    }}
  />
);

const BlogPost = ({ post, onBack }) => {
  const [imageError, setImageError] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  if (!post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          {[...Array(8)].map((_, i) => (
            <FloatingParticle key={i} delay={i * 0.5} />
          ))}
          <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-3xl animate-pulse" />
          <div
            className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "2s" }}
          />
        </div>

        <motion.div
          className="text-center relative z-10"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-16 max-w-lg mx-auto shadow-2xl">
            <motion.div
              className="w-20 h-20 bg-gradient-to-r from-red-500 to-pink-500 rounded-full mx-auto mb-8 flex items-center justify-center"
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <span className="text-3xl">📖</span>
            </motion.div>
            <h2 className="text-3xl font-bold text-white mb-6">
              Article Not Found
            </h2>
            <p className="text-gray-100 mb-8 leading-relaxed">
              The article you're looking for doesn't exist or has been moved.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 font-semibold shadow-lg shadow-blue-500/25"
            >
              <FaArrowLeft />
              Back to Blog
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Create a default blog article image as SVG data URI
  const defaultBlogImage = `data:image/svg+xml,${encodeURIComponent(`
    <svg width="1200" height="600" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:#1f2937;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#374151;stop-opacity:1" />
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#grad1)"/>
      <text x="50%" y="45%" dominant-baseline="middle" text-anchor="middle" fill="#60a5fa" font-family="system-ui" font-size="48" font-weight="bold">Blog Article</text>
      <text x="50%" y="60%" dominant-baseline="middle" text-anchor="middle" fill="#9ca3af" font-family="system-ui" font-size="24">Security Insights</text>
    </svg>
  `)}`;

  // Create a default avatar image as SVG data URI
  const defaultAvatar = `data:image/svg+xml,${encodeURIComponent(`
    <svg width="64" height="64" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="avatarGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:#3b82f6;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#8b5cf6;stop-opacity:1" />
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#avatarGrad)" rx="32"/>
      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="white" font-family="system-ui" font-size="24" font-weight="bold">MS</text>
    </svg>
  `)}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <FloatingParticle key={i} delay={i * 0.8} />
        ))}
        <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "3s" }}
        />
      </div>

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Enhanced Back Button */}
        <motion.button
          onClick={onBack}
          className="inline-flex items-center gap-3 px-6 py-3 mb-12 bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl text-gray-100 hover:bg-white/20 hover:text-white transition-all shadow-lg hover:shadow-blue-500/25 group"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          whileHover={{ scale: 1.05 }}
        >
          <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back to Blog</span>
        </motion.button>

        {/* Article Container */}
        <motion.article
          className="max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Hero Image */}
          {(post.image || !imageError) && (
            <motion.div
              className="relative overflow-hidden rounded-3xl mb-12 h-96 md:h-[500px] shadow-2xl"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <img
                src={imageError ? defaultBlogImage : appleImage}
                alt={post.title}
                className="w-full h-full object-contain bg-gray-800"
                onError={(e) => {
                  if (!imageError) {
                    setImageError(true);
                    e.target.src = defaultBlogImage;
                  }
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent"></div>

              {/* Floating Action Buttons */}
              <div className="absolute top-6 right-6 flex gap-3">
                <motion.button
                  className="p-3 bg-black/50 backdrop-blur-xl rounded-xl border border-white/20 text-white hover:bg-white/20 transition-all"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaBookmark />
                </motion.button>
                <motion.button
                  className="p-3 bg-black/50 backdrop-blur-xl rounded-xl border border-white/20 text-white hover:bg-white/20 transition-all"
                  whileHover={{ scale: 1.1, rotate: -5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaShare />
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Article Header */}
          <div className="mb-12">
            {/* Meta Information */}
            <motion.div
              className="flex flex-wrap items-center gap-6 text-sm text-gray-200 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full border border-white/10">
                <FaCalendarAlt className="text-blue-400" />
                <span>
                  {new Date(post.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full border border-white/10">
                <FaClock className="text-purple-400" />
                <span>{post.readTime || "5 min read"}</span>
              </div>


            </motion.div>

            {/* Title */}
            <motion.h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 md:mb-8 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                {post.title}
              </span>
            </motion.h1>

            {/* Excerpt */}
            {post.excerpt && (
              <motion.p
                className="text-lg sm:text-xl md:text-2xl text-gray-100 leading-relaxed font-light max-w-4xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                {post.excerpt}
              </motion.p>
            )}
          </div>

          {/* Article Content */}
          <motion.div
            className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl border border-white/20 rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-2xl"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <div className="prose prose-xl prose-invert max-w-none blog-post-content">
              <BlogContent content={post.content} />
            </div>
          </motion.div>

          {/* Article Footer */}
          <motion.div
            className="mt-16 pt-12 border-t border-white/20"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
          >
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-8">
              {/* Author Info */}
              <div className="flex items-center gap-4 md:gap-6">
                <motion.img
                  src={avatarError ? defaultAvatar : "/author-avatar.jpg"}
                  alt="Mihir Shah"
                  className="w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-white/20"
                  onError={(e) => {
                    if (!avatarError) {
                      setAvatarError(true);
                      e.target.src = defaultAvatar;
                    }
                  }}
                  whileHover={{ scale: 1.1 }}
                />
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-white mb-1">
                    Mihir Shah
                  </h3>
                  <p className="text-sm md:text-base text-gray-200">
                    Security Engineer & Author
                  </p>
                </div>
              </div>


            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <motion.div
                className="mt-12 pt-8 border-t border-white/10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4 }}
              >
                <h4 className="text-lg font-semibold text-white mb-4">
                  Related Topics
                </h4>
                <div className="flex flex-wrap gap-3">
                  {post.tags.map((tag, index) => (
                    <motion.span
                      key={index}
                      className="px-4 py-2 bg-white/5 border border-white/20 text-gray-100 rounded-full hover:bg-white/10 transition-all cursor-pointer font-medium"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.4 + index * 0.1 }}
                      whileHover={{ scale: 1.05, y: -2 }}
                    >
                      #{tag}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>
        </motion.article>

        {/* Related Articles Suggestion */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.6 }}
        >
          <div className="bg-gradient-to-br from-blue-900/30 via-purple-900/30 to-pink-900/30 backdrop-blur-xl border border-white/20 rounded-3xl p-12 max-w-3xl mx-auto shadow-2xl">
            <h3 className="text-3xl font-bold text-white mb-6">
              Explore More Insights
            </h3>
            <p className="text-xl text-gray-100 mb-8 leading-relaxed">
              Discover more articles on cloud security, OWASP VXDF, and
              cutting-edge cybersecurity research
            </p>
            <motion.button
              onClick={onBack}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-2xl transition-all transform hover:scale-105 font-semibold text-lg shadow-lg shadow-blue-500/25"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>View All Articles</span>
              <FaArrowLeft className="rotate-180" />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BlogPost;
